import React from 'react';
import { find } from 'ramda';
import { compose, defaultProps, pure, withHandlers, withProps, withState } from 'recompose';
import { withClickOutside } from '../../HOCs/withClickOutside';
import { withTheme } from '../../utils/styling/styled-components';
import { DefaultOption, SubtextOption } from './options';
import { Value } from './styled-components';

var focusListItem = function focusListItem(list, options, value) {
  /* istanbul ignore else */
  if (list && options && value) {
    var selectedIndex = options.findIndex(function (option) {
      return option.value === value;
    });
    var activeItem = list.children[selectedIndex];

    if (activeItem) {
      activeItem.focus();
    }
  }
};

var currentIndex = function currentIndex(props) {
  return props.options.findIndex(function (option) {
    return option.value === props.keyboardSelected;
  });
};

var moveToOption = function moveToOption(newValue, props) {
  /* istanbul ignore else */
  if (newValue) {
    props.setKeyboardSelected(newValue);
    focusListItem(props.listElement, props.options, newValue);
  }
};

var searchOptionByKeyboard = function searchOptionByKeyboard(key, props, event) {
  var searchText = props.keyboardSearch || '';
  /* istanbul ignore else */

  if (props.keyboardSearchTime !== event.timeStamp) {
    props.setKeyboardSearchTime(event.timeStamp);
  }

  var timeSpan = event.timeStamp - (props.keyboardSearchTime || event.timeStamp);

  if (timeSpan < 500) {
    searchText += key;
  } else {
    searchText = key;
  }

  props.setKeyboardSearch(searchText);
  var nextOption = props.options.find(function (opt, i) {
    return opt.label.toLowerCase().startsWith(searchText) && i > currentIndex(props);
  });

  if (!nextOption) {
    nextOption = props.options.find(function (opt) {
      return opt.label.toLowerCase().startsWith(searchText);
    });
  }

  if (!nextOption) {
    props.setKeyboardSearch(null);
  }

  return nextOption;
};

export var processKeyDownEvent = function processKeyDownEvent(props, event) {
  var normalizedKey = event.key.toLowerCase();

  if (!props.active) {
    switch (normalizedKey) {
      case 'arrowdown':
      case ' ':
        {
          event.preventDefault();
          event.stopPropagation();
          props.toggleActive(true);
          /* istanbul ignore else */

          if (!props.keyboardSelected && props.selectedOption) {
            moveToOption(props.selectedOption.value, props);
          }
        }
        break;

      case 'escape':
        {
          event.preventDefault();
          event.stopPropagation();
          props.selectElement.focus();
        }
        break;

      default:
        {
          /* istanbul ignore else */
          if (/^[a-z]{1}$/.test(normalizedKey)) {
            event.preventDefault();
            event.stopPropagation();
            props.toggleActive(true);
            var option = searchOptionByKeyboard(normalizedKey, props, event);

            if (option) {
              moveToOption(option.value, props);
            }
          }
        }
    }
  } else {
    switch (normalizedKey) {
      case 'tab':
        {
          props.toggleActive(false);
        }
        break;

      case 'escape':
        {
          event.preventDefault();
          event.stopPropagation();
          props.toggleActive(false);
          props.selectElement.focus();
        }
        break;

      case 'enter':
        {
          /* istanbul ignore else */
          if (props.keyboardSelected) {
            event.preventDefault();
            event.stopPropagation();
            var field = props.name || props.id;
            props.onChange(field, props.keyboardSelected);
            props.toggleActive(false);
            props.selectElement.focus();
          }
        }
        break;

      case 'arrowdown':
        {
          event.preventDefault();
          event.stopPropagation();

          if (currentIndex(props) + 1 < props.options.length) {
            var _option = props.options[currentIndex(props) + 1];
            moveToOption(_option.value, props);
          }
        }
        break;

      case 'arrowup':
        {
          event.preventDefault();
          event.stopPropagation();

          if (currentIndex(props) - 1 >= 0) {
            var _option2 = props.options[currentIndex(props) - 1];
            moveToOption(_option2.value, props);
          }
        }
        break;

      default:
        {
          /* istanbul ignore else */
          if (/^[a-z]{1}$/.test(normalizedKey)) {
            event.preventDefault();
            event.stopPropagation();

            var _option3 = searchOptionByKeyboard(normalizedKey, props, event);

            if (_option3) {
              moveToOption(_option3.value, props);
            }
          }
        }
    }
  }
};
var enhance = compose(withTheme, defaultProps({
  disabled: false,
  position: 'bottom',
  hasHiddenLabel: false,
  defaultLabel: '',
  renderSelected: function renderSelected(props) {
    return /*#__PURE__*/React.createElement(Value, {
      "data-error": props.error
    }, props.selectedLabel);
  },
  useNative: /iPhone|iPod|Android/i.test(navigator.userAgent),
  showResponsiveOverlay: true
}), withProps(function () {
  return {
    renderOption: function renderOption(label, selected, _, subtext, className) {
      return !subtext ? /*#__PURE__*/React.createElement(DefaultOption, {
        label: label,
        selected: selected,
        className: className
      }) : /*#__PURE__*/React.createElement(SubtextOption, {
        label: label,
        selected: selected,
        subtext: subtext
      });
    }
  };
}), withState('active', 'setActive', false), withState('keyboardSelected', 'setKeyboardSelected', null), withState('keyboardSearch', 'setKeyboardSearch', null), withState('keyboardSearchTime', 'setKeyboardSearchTime', 0), withState('container', 'setContainer', null), withState('listElement', 'setListElement', null), withState('offsetTop', 'setOffsetTop', null), withState('selectElement', 'setSelectElement', null), withProps(function (_ref) {
  var options = _ref.options,
      value = _ref.value,
      defaultLabel = _ref.defaultLabel;
  var selectedOption = options.find(function (option) {
    return option.value === value;
  });
  return {
    selectedLabel: selectedOption ? selectedOption.label : defaultLabel,
    selectedOption: selectedOption
  };
}), withHandlers({
  toggleActive: function toggleActive(props) {
    return function (isActive) {
      props.setActive(isActive);
      props.onToggle && props.onToggle(isActive);

      if (!props.useNative && props.toggleInputs) {
        props.toggleInputs(isActive);
      }

      if (!isActive) {
        props.setKeyboardSelected(null);
      }
    };
  }
}), withHandlers({
  closeInput: function closeInput(_ref2) {
    var toggleActive = _ref2.toggleActive;
    return function () {
      return toggleActive(false);
    };
  },
  handleChange: function handleChange(_ref3) {
    var onChange = _ref3.onChange;
    return function (_ref4) {
      var target = _ref4.target;
      var field = target.name || target.id;
      onChange(field, target.value);
    };
  },
  handleContainerRef: function handleContainerRef(_ref5) {
    var setContainer = _ref5.setContainer;
    return function (element) {
      setContainer(element);
    };
  },
  handleKeyDown: function handleKeyDown(props) {
    return function (e) {
      if (!props.useNative && !props.disabled) {
        processKeyDownEvent(props, e);
      }
    };
  },
  handleKeyUp: function handleKeyUp(_ref6) {
    var listElement = _ref6.listElement,
        useNative = _ref6.useNative;
    return function (_ref7) {
      var key = _ref7.key;
      var normalizedKey = key.toLowerCase();

      if (/[a-z]/.test(normalizedKey) && !useNative) {
        var itemNodes = Array.from(listElement.querySelectorAll('li'));
        var activeItem = find(function (item) {
          return item.dataset.value[0].toLowerCase() === normalizedKey;
        }, itemNodes);

        if (activeItem) {
          listElement.scrollTo(0, activeItem.offsetTop);
        }
      }
    };
  },
  handleListRef: function handleListRef(_ref8) {
    var setListElement = _ref8.setListElement,
        options = _ref8.options,
        value = _ref8.value,
        keyboardSelected = _ref8.keyboardSelected;
    return function (list) {
      setListElement(list);
      focusListItem(list, options, keyboardSelected || value);
    };
  },
  handleMouseDown: function handleMouseDown(_ref9) {
    var toggleActive = _ref9.toggleActive,
        useNative = _ref9.useNative,
        disabled = _ref9.disabled;
    return function () {
      if (!useNative && !disabled) {
        toggleActive(true);
      }
    };
  },
  handleOptionClick: function handleOptionClick(_ref10) {
    var id = _ref10.id,
        name = _ref10.name,
        onChange = _ref10.onChange,
        toggleActive = _ref10.toggleActive;
    return function (_ref11) {
      var currentTarget = _ref11.currentTarget;
      var field = name || id;
      onChange(field, currentTarget.dataset.value);
      toggleActive(false);
    };
  },
  handleOverlayClick: function handleOverlayClick(_ref12) {
    var toggleActive = _ref12.toggleActive;
    return function () {
      return toggleActive(false);
    };
  },
  handleSelectRef: function handleSelectRef(_ref13) {
    var setSelectElement = _ref13.setSelectElement;
    return function (element) {
      setSelectElement(element);
    };
  }
}), withClickOutside(function (_ref14) {
  var toggleActive = _ref14.toggleActive,
      clickTarget = _ref14.clickTarget,
      openRefs = _ref14.openRefs;
  var isInOpenRefs = openRefs === null || openRefs === void 0 ? void 0 : openRefs.some(function (ref) {
    var _ref$current;

    return (ref === null || ref === void 0 ? void 0 : ref.current) === clickTarget || (ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.contains(clickTarget));
  });

  if (!isInOpenRefs) {
    toggleActive(false);
  }
}, 'mousedown'), pure);
export { enhance };